<script>
import { mapGetters } from 'vuex'
import formatDate from '@utils/format-date'

export default {
  props: {
    disableEdit: { type: Boolean, default: false },
    maximumInfantAge: { type: Number, required: true },
  },
  computed: {
    ...mapGetters('cart', ['searchParams', 'breakdown']),
    ...mapGetters('cart/rooms', { roomCartItems: 'cartItems' }),
    ...mapGetters('cart/extras', { extraCartItems: 'cartItems' }),
    topFields() {
      return [
        { label: 'Check-in', value: formatDate(this.searchParams.checkIn) },
        { label: 'Check-out', value: formatDate(this.searchParams.checkOut) },
      ]
    },
    bottomFields() {
      const currency = this.breakdown.currency

      return [
        { label: 'Total', value: this.breakdown.net, currency },
        {
          label: 'Discount',
          value: this.breakdown.discount,
          class: this.breakdown.discount ? '' : 'hidden',
          currency,
        },
        { label: 'Tax', value: this.breakdown.tax, currency },
        { label: 'Service', value: this.breakdown.service, currency },
        {
          label: 'Grand total',
          value: this.breakdown.gross,
          class: 'text-gold-500',
          currency,
        },
      ]
    },
  },
}
</script>

<template>
  <BaseBox title="Booking summary">
    <ul class="text-sm">
      <li
        v-for="field in topFields"
        :key="field.label"
        class="border-b border-dotted border-gray-400 mb-3"
      >
        <div class="flex flex-wrap font-medium">
          <span class="w-2/3">{{ field.label }}</span>
          <span class="w-1/3 text-right">{{ field.value }}</span>
        </div>
      </li>
      <li>
        <ul>
          <li
            v-for="roomItem in roomCartItems"
            :key="roomItem.name"
            class="border-b border-dotted border-gray-400 mb-3"
          >
            <div class="flex flex-wrap">
              <div class="w-2/3">
                <div class="flex">
                  <span class="mr-1 font-medium">{{ roomItem.qty }}x</span>
                  <div class="flex flex-col">
                    <span class="font-medium">
                      {{ roomItem.name }}
                    </span>
                    <ul>
                      <li class="flex">
                        <span class="text-gray-600 mr-3">
                          <i class="fas fa-user"></i>
                        </span>
                        <span v-if="roomItem.occupancy.extraBeds > 0">
                          Max
                          {{
                            roomItem.occupancy.included | pluralize('adult')
                          }}, 1 kid (0-{{
  maximumInfantAge | pluralize('year')
}})
                        </span>
                        <span v-else>
                          Max
                          {{ roomItem.occupancy.included | pluralize('adult') }}
                        </span>
                      </li>
                      <li class="flex">
                        <span class="text-green-600 mr-3">
                          <i class="fas fa-check"></i>
                        </span>
                        <span>
                          Free breakfast for {{ roomItem.occupancy.included }}
                        </span>
                      </li>
                      <li v-if="roomItem.benefits.length" class="flex">
                        <span class="text-green-600 mr-3">
                          <i class="fas fa-check"></i>
                        </span>
                        <span>{{ roomItem.benefits.join(', ') }}</span>
                      </li>
                      <li v-if="roomItem.wifiIncluded" class="flex">
                        <span class="text-green-600 mr-3">
                          <i class="fas fa-check"></i>
                        </span>
                        <span>Free Wi-Fi</span>
                      </li>
                      <li class="flex">
                        <span class="text-green-600 mr-3">
                          <i class="fas fa-check"></i>
                        </span>
                        <span>{{ roomItem.cancellation }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="w-1/3">
                <div class="flex flex-col items-end">
                  <span class="font-medium">
                    {{ roomItem.price.currency }}
                    {{ roomItem.price.net | currency }}
                  </span>
                  <BaseLink
                    v-if="!disableEdit"
                    class="text-gold-500 underline cursor-pointer"
                    :to="{ name: 'property-landing', query: $route.query }"
                  >
                    (edit)
                  </BaseLink>
                </div>
              </div>
            </div>
            <ul v-if="roomItem.extrabed.length">
              <li v-for="extrabed in roomItem.extrabed" :key="extrabed.name">
                <div class="flex flex-wrap">
                  <div class="w-2/3">
                    <div class="flex">
                      <span class="mr-1 font-medium">{{ extrabed.qty }}x</span>
                      <div class="flex flex-col">
                        <span class="font-medium">
                          {{ extrabed.name }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="w-1/3 text-right">
                    <span class="font-medium">
                      {{ extrabed.price.currency }}
                      {{ extrabed.price.net | currency }}
                    </span>
                  </div>
                </div>
              </li>
            </ul>
          </li>
        </ul>
      </li>
      <li>
        <ul>
          <li
            v-for="extraItem in extraCartItems"
            :key="extraItem.id"
            class="border-b border-dotted border-gray-400 mb-3"
          >
            <div class="flex flex-wrap">
              <div class="w-2/3">
                <div class="flex">
                  <span class="mr-1 font-medium">{{ extraItem.qty }}x</span>
                  <div class="flex flex-col">
                    <span class="font-medium">{{ extraItem.name }}</span>
                    <span>
                      on: {{ extraItem.date | formatDate('DD MMM YYYY') }} @{{
                        extraItem.time
                      }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="w-1/3">
                <div class="flex flex-col items-end">
                  <span class="font-medium">
                    {{ extraItem.price.currency }}
                    {{ extraItem.price.net | currency }}
                  </span>
                  <BaseLink
                    v-if="!disableEdit"
                    class="text-gold-500 underline cursor-pointer"
                    :to="{ name: 'property-extras', query: $route.query }"
                  >
                    (edit)
                  </BaseLink>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </li>
      <li
        v-for="field in bottomFields"
        :key="field.label"
        class="border-b border-dotted border-gray-400 mb-3"
        :class="field.class"
      >
        <div class="flex flex-wrap font-medium">
          <span class="w-1/2">{{ field.label }}</span>
          <span class="w-1/2 text-right">
            {{ field.currency }} {{ field.value | currency }}
          </span>
        </div>
      </li>
    </ul>
  </BaseBox>
</template>
